/**
 * Collection Buffer Stock Filter
 * 
 * Hides products from collection pages when ALL variants are in buffer stock range.
 * This integrates with Shopify's "Only show available" filter to properly hide
 * products that appear available but are actually unavailable due to buffer stock.
 */

(function() {
  'use strict';

  // Get buffer stock settings from window or use defaults
  const BUFFER_STOCK_ENABLED = window.bufferStockSettings?.enabled || false;
  const BUFFER_STOCK_LIMIT = window.bufferStockSettings?.limit || 2;
  const DEBUG = window.bufferStockSettings?.debug || false;

  // Only run if buffer stock is enabled
  if (!BUFFER_STOCK_ENABLED) {
    if (DEBUG) console.log('[Buffer Stock Filter] Disabled - skipping');
    return;
  }

  // Check if we're on a filtered collection with availability filter
  const urlParams = new URLSearchParams(window.location.search);
  const hasAvailabilityFilter = urlParams.get('filter.v.availability') === '1';

  if (DEBUG) {
    console.log('[Buffer Stock Filter] Settings:', {
      enabled: BUFFER_STOCK_ENABLED,
      limit: BUFFER_STOCK_LIMIT,
      hasAvailabilityFilter: hasAvailabilityFilter,
      url: window.location.href
    });
  }

  /**
   * Check if a product should be hidden based on buffer stock
   * @param {Element} productCard - The product card element
   * @returns {boolean} - True if product should be hidden
   */
  function shouldHideProduct(productCard) {
    try {
      // Get the product handle
      const handle = productCard.getAttribute('handle');
      if (!handle) return false;

      // Find all variant inputs with buffer stock data
      const variantInputs = productCard.querySelectorAll('[data-variant-id]');
      
      if (variantInputs.length === 0) {
        if (DEBUG) console.log(`[Buffer Stock Filter] ${handle}: No variant inputs found`);
        return false;
      }

      let totalVariants = 0;
      let bufferStockVariants = 0;
      let soldOutVariants = 0;

      variantInputs.forEach(input => {
        totalVariants++;
        
        const isBufferStock = input.getAttribute('data-buffer-stock') === 'true';
        const isDisabled = input.disabled || input.getAttribute('disabled') !== null;
        
        if (isBufferStock || isDisabled) {
          bufferStockVariants++;
        }
        
        if (isDisabled && !isBufferStock) {
          soldOutVariants++;
        }
      });

      const allVariantsUnavailable = bufferStockVariants === totalVariants;

      if (DEBUG) {
        console.log(`[Buffer Stock Filter] ${handle}:`, {
          totalVariants,
          bufferStockVariants,
          soldOutVariants,
          shouldHide: allVariantsUnavailable && hasAvailabilityFilter
        });
      }

      // Hide product if:
      // 1. Availability filter is active
      // 2. ALL variants are either buffer stock or sold out
      return hasAvailabilityFilter && allVariantsUnavailable;

    } catch (error) {
      console.error('[Buffer Stock Filter] Error checking product:', error);
      return false;
    }
  }

  /**
   * Filter products on the collection page
   */
  function filterProducts() {
    // Find all product cards
    const productCards = document.querySelectorAll('product-card[handle]');
    
    if (productCards.length === 0) {
      if (DEBUG) console.log('[Buffer Stock Filter] No product cards found');
      return;
    }

    let hiddenCount = 0;

    productCards.forEach(card => {
      if (shouldHideProduct(card)) {
        card.style.display = 'none';
        card.setAttribute('data-hidden-by-buffer-stock', 'true');
        hiddenCount++;
      } else {
        // Ensure it's visible if it was previously hidden
        if (card.getAttribute('data-hidden-by-buffer-stock')) {
          card.style.display = '';
          card.removeAttribute('data-hidden-by-buffer-stock');
        }
      }
    });

    if (DEBUG) {
      console.log(`[Buffer Stock Filter] Filtered ${hiddenCount} of ${productCards.length} products`);
    }

    // Update product count if it exists
    updateProductCount(hiddenCount);
  }

  /**
   * Update the product count display
   */
  function updateProductCount(hiddenCount) {
    const countElement = document.querySelector('.collection-toolbar__products-count');
    if (!countElement) return;

    const originalText = countElement.textContent;
    const match = originalText.match(/(\d+)/);
    
    if (match) {
      const originalCount = parseInt(match[1]);
      const newCount = originalCount - hiddenCount;
      
      if (newCount !== originalCount) {
        countElement.textContent = originalText.replace(/\d+/, newCount);
        
        if (DEBUG) {
          console.log(`[Buffer Stock Filter] Updated count: ${originalCount} → ${newCount}`);
        }
      }
    }
  }

  /**
   * Initialize the filter
   */
  function init() {
    // Run immediately
    filterProducts();

    // Re-run after a short delay to catch lazy-loaded content
    setTimeout(filterProducts, 500);
    setTimeout(filterProducts, 1000);

    // Watch for dynamic content changes (infinite scroll, filtering, etc.)
    const observer = new MutationObserver(() => {
      filterProducts();
    });

    const productList = document.querySelector('.product-list, [id^="product-list"]');
    if (productList) {
      observer.observe(productList, {
        childList: true,
        subtree: true
      });
    }

    // Re-run when URL changes (for filtering/sorting)
    window.addEventListener('popstate', () => {
      setTimeout(filterProducts, 300);
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
